import { useRouter } from 'vue-router'
import { useAuthStore } from '@state/stores/auth'
import { useFavoritesStore } from '@state/stores/favorites'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMessages } from '@composables/use-messages'

export type FavouriteType = 'mixes' | 'artists' | 'shows'

interface FavouriteRef {
    type: FavouriteType
    id: number
}

interface ToggleOptions extends FavouriteRef {
    addedKey: string
    removedKey: string
}

const LIST_KEY: Record<FavouriteType, 'mixesList' | 'artistsList' | 'showsList'> = {
    mixes: 'mixesList',
    artists: 'artistsList',
    shows: 'showsList',
}

interface UseFavouriteToggle {
    isFavourite: (ref: FavouriteRef) => boolean
    toggle: (options: ToggleOptions) => Promise<void>
}

// The favourite/follow action a menu row runs. Mirrors _base-favourites.vue's
// auth gate and notifications so a menu row behaves exactly like the heart.
// (T10 Badges folds BaseFavourites onto this composable.)
export function useFavouriteToggle(): UseFavouriteToggle {
    const router = useRouter()
    const authStore = useAuthStore()
    const favoritesStore = useFavoritesStore()
    const appearanceStore = useAppearanceStore()
    const getMessageText = useMessages()
    // Instance-scoped: one Set per menu row's composable instance, never module
    // scope — a module-level Set would be shared SSR request-to-request/user-to-user.
    // Keyed per entity so an in-flight toggle only blocks a repeat click on the
    // SAME row; a different row's entity is never blocked by this.
    const pending = new Set<string>()

    function keyFor({ type, id }: FavouriteRef): string {
        return `${type}:${id}`
    }

    function isFavourite({ type, id }: FavouriteRef): boolean {
        const list = favoritesStore[LIST_KEY[type]] as Array<{ id: number }>
        return list.some(entry => entry.id === id)
    }

    // favorites/appearance stores are untyped `.ts` files, so Pinia infers their
    // action params from destructuring — `handleNotification` picks up every
    // destructured key as required. Cast here rather than editing the store.
    function notify(message: string, type: 'success' | 'warning'): void {
        appearanceStore.handleNotification({ message, type } as Parameters<typeof appearanceStore.handleNotification>[0])
    }

    async function toggle({ type, id, addedKey, removedKey }: ToggleOptions): Promise<void> {
        if (!authStore.loggedIn) {
            router.push({ name: 'login' })
            return
        }

        const key = keyFor({ type, id })
        if (pending.has(key)) return
        pending.add(key)

        const wasFavourite = isFavourite({ type, id })
        try {
            if (wasFavourite) await favoritesStore.remove({ type, id })
            else await favoritesStore.add({ type, id })
            notify(getMessageText(wasFavourite ? removedKey : addedKey), 'success')
        } catch (error) {
            console.error(error)
            notify(getMessageText('SOMETHING_WENT_WRONG'), 'warning')
        } finally {
            pending.delete(key)
        }
    }

    return { isFavourite, toggle }
}
